import { useState, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { logEntityChanges } from '../utils/logger';
import { sanitizeString } from '../utils/sanitization';
import { getOpenTasksForOpportunityStage } from '../utils/associationUtils';

const STAGE_ORDER = ['New', 'Qualified', 'Proposal Sent', 'Closed Won', 'Not Qualified', 'Closed Lost'];
const FOLLOWUP_STAGES = ['Not Qualified', 'Closed Lost'];

/**
 * Shared hook for opportunity stage-change logic used by both DashboardPage and OppPage.
 *
 * Handles:
 *  - Potential Value validation (can't leave "New" without a value)
 *  - Actual Value modal prompt when moving to "Closed Won"
 *  - Follow-Up modal prompt when moving to "Not Qualified" or "Closed Lost"
 *  - Open-tasks warning when advancing stages
 *  - Firestore update + activity-log write
 *  - Gamification point awards (needs recordActivity from GamificationContext)
 *
 * Returns state objects for the Actual Value modal, Follow-Up modal & Open Tasks warning modal,
 * plus the main `handleStageChange` function that should be passed to
 * `<PipelineBoard onStageChange={handleStageChange}>`.
 *
 * @param {Object}   options
 * @param {Object}   options.user            - Authenticated user (from useAuth or prop)
 * @param {Function} options.setToast        - Toast callback ({ message, type })
 * @param {Function} [options.recordActivity] - Gamification recordActivity (optional)
 */
export default function useStageChange({ user, setToast, recordActivity }) {
  // Actual Value modal state
  const [actualValueModal, setActualValueModal] = useState({ show: false, opp: null });
  const [pendingActualValue, setPendingActualValue] = useState('');

  // Follow-Up modal state (for Not Qualified / Closed Lost)
  const [followUpModal, setFollowUpModal] = useState({ show: false, opp: null, newStage: null });
  const [pendingFollowUp, setPendingFollowUp] = useState({ reason: '', flag: false, date: '', notes: '' });
  const [followUpError, setFollowUpError] = useState('');

  // Open Tasks warning modal state
  const [showTaskWarning, setShowTaskWarning] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [openTasksData, setOpenTasksData] = useState({ openTasks: [], count: 0 });

  // Core update — writes to Firestore, logs, and awards points
  const performStageChange = useCallback(async (opp, newStage, actualValue = null, followUpData = null) => {
    try {
      const updateData = { stage: newStage, updatedAt: serverTimestamp() };
      if (newStage === 'Closed Won' && actualValue !== null) {
        updateData.actualValue = parseFloat(actualValue);
      }

      // Write follow-up fields when moving to Not Qualified / Closed Lost
      if (FOLLOWUP_STAGES.includes(newStage) && followUpData) {
        updateData.stageChangeReason = sanitizeString(followUpData.reason || '', 1000);
        updateData.followUpFlag = !!followUpData.flag;
        updateData.followUpDate = followUpData.flag ? sanitizeString(followUpData.date, 20) : '';
        updateData.followUpNotes = followUpData.flag ? sanitizeString(followUpData.notes, 1000) : '';
        if (followUpData.flag) {
          updateData.followUpSetByUid = user.uid;
          updateData.followUpSetByName = user.displayName || user.email;
          updateData.followUpSetAt = serverTimestamp();
          updateData.followUpCompleted = false;
          updateData.followUpCompletedAt = null;
          updateData.followUpCompletedByUid = null;
        }
      }

      await updateDoc(doc(db, collectionPath('opportunities'), opp.id), updateData);

      await logEntityChanges({
        oldObj: opp,
        newObj: { ...opp, ...updateData },
        fields: [
          ...(newStage === 'Closed Won' && actualValue ? ['stage', 'actualValue'] : ['stage']),
          ...(followUpData?.reason ? ['stageChangeReason'] : []),
          ...(followUpData?.flag ? ['followUpFlag', 'followUpDate', 'followUpNotes'] : []),
        ],
        entityType: 'Opportunity',
        entityId: opp.id,
        user,
        action: 'Stage Changed',
        collectionName: collectionPath('logsOpp'),
      });

      // Gamification (optional — DashboardPage may not have context yet)
      if (recordActivity) {
        try {
          const oldIndex = STAGE_ORDER.indexOf(opp.stage);
          const newIndex = STAGE_ORDER.indexOf(newStage);
          const isAdvancement = newIndex > oldIndex && newStage !== 'Closed Lost' && newStage !== 'Not Qualified';

          if (newStage === 'Closed Won') {
            const dealValue = actualValue
              ? parseFloat(actualValue)
              : await (async () => {
                  const { getValueRangeMidpoint } = await import('../components/forms/OppModal');
                  return getValueRangeMidpoint(opp.value);
                })();
            await recordActivity('OPPORTUNITY_WON', {
              dealValue,
              entityId: opp.id,
              source: 'manual',
            });
          } else if (isAdvancement) {
            await recordActivity('OPPORTUNITY_STAGE_ADVANCED', {
              entityId: opp.id,
              source: 'manual',
              oldStage: opp.stage,
              newStage,
            });
          }
        } catch (e) {
          console.warn('Gamification failed:', e);
        }
      }

      setToast?.({ message: 'Opportunity stage updated', type: 'success' });
    } catch (err) {
      console.error('Failed to update opportunity stage:', err);
      setToast?.({ message: 'Failed to update stage: ' + err.message, type: 'error' });
    }
  }, [user, setToast, recordActivity]);

  // Top-level handler passed to PipelineBoard / stage dropdowns
  const handleStageChange = useCallback(async (opp, newStage) => {
    // 1. Validate Potential Value when leaving "New"
    if (opp.stage === 'New' && newStage !== 'New' && !opp.value) {
      setToast?.({
        message: 'Potential Value is required when advancing from "New" stage. Please edit the opportunity and select a value range.',
        type: 'error',
      });
      return;
    }

    // 2. Prompt for Actual Value when moving to "Closed Won"
    if (newStage === 'Closed Won') {
      setActualValueModal({ show: true, opp });
      setPendingActualValue('');
      return;
    }

    // 2b. Prompt for Follow-Up when moving to "Not Qualified" or "Closed Lost"
    if (FOLLOWUP_STAGES.includes(newStage)) {
      setFollowUpModal({ show: true, opp, newStage });
      setPendingFollowUp({ reason: '', flag: false, date: '', notes: '' });
      setFollowUpError('');
      return;
    }

    // 3. Check for open tasks when advancing forward
    const oldIndex = STAGE_ORDER.indexOf(opp.stage);
    const newIndex = STAGE_ORDER.indexOf(newStage);

    if (newIndex > oldIndex && oldIndex >= 0) {
      try {
        const result = await getOpenTasksForOpportunityStage(opp.id, opp.stage);
        if (result.hasOpenTasks) {
          setOpenTasksData(result);
          setPendingStageChange({ opp, newStage });
          setShowTaskWarning(true);
          return;
        }
      } catch (err) {
        console.warn('Error checking open tasks:', err);
      }
    }

    // 4. No blockers — execute the change
    await performStageChange(opp, newStage, null);
  }, [performStageChange, setToast]);

  // Called when user submits the Actual Value modal
  const handleActualValueSubmit = useCallback(async () => {
    const { opp } = actualValueModal;
    if (!pendingActualValue || parseFloat(pendingActualValue) <= 0) {
      setToast?.({ message: 'Please enter a valid actual value', type: 'error' });
      return;
    }
    setActualValueModal({ show: false, opp: null });
    await performStageChange(opp, 'Closed Won', pendingActualValue);
  }, [actualValueModal, pendingActualValue, performStageChange, setToast]);

  // Called when user submits the Follow-Up modal (Not Qualified / Closed Lost)
  const handleFollowUpSubmit = useCallback(async () => {
    const { opp, newStage } = followUpModal;
    if (!opp || !newStage) return;
    if (!pendingFollowUp.reason?.trim()) {
      setFollowUpError('A reason for this stage change is required.');
      return;
    }
    setFollowUpError('');
    setFollowUpModal({ show: false, opp: null, newStage: null });
    await performStageChange(opp, newStage, null, pendingFollowUp);
  }, [followUpModal, pendingFollowUp, performStageChange]);

  // Called when user skips the Follow-Up modal (proceeds without flagging)
  const handleFollowUpSkip = useCallback(async () => {
    const { opp, newStage } = followUpModal;
    if (!opp || !newStage) return;
    if (!pendingFollowUp.reason?.trim()) {
      setFollowUpError('A reason for this stage change is required.');
      return;
    }
    setFollowUpError('');
    setFollowUpModal({ show: false, opp: null, newStage: null });
    await performStageChange(opp, newStage, null, { reason: pendingFollowUp.reason, flag: false, date: '', notes: '' });
  }, [followUpModal, pendingFollowUp, performStageChange]);

  // Called when user cancels the Follow-Up modal (returns to previous stage)
  const handleFollowUpCancel = useCallback(() => {
    setFollowUpModal({ show: false, opp: null, newStage: null });
    setPendingFollowUp({ reason: '', flag: false, date: '', notes: '' });
    setFollowUpError('');
  }, []);

  // Called when user confirms the Open Tasks warning ("Advance Anyway")
  const handleConfirmStageChange = useCallback(async () => {
    if (!pendingStageChange) return;
    const { opp, newStage } = pendingStageChange;
    setShowTaskWarning(false);
    setPendingStageChange(null);
    setOpenTasksData({ openTasks: [], count: 0 });
    await performStageChange(opp, newStage, null);
  }, [pendingStageChange, performStageChange]);

  // Called when user cancels the Open Tasks warning
  const handleCancelStageChange = useCallback(() => {
    setShowTaskWarning(false);
    setPendingStageChange(null);
    setOpenTasksData({ openTasks: [], count: 0 });
  }, []);

  return {
    handleStageChange,

    // Actual Value modal
    actualValueModal,
    setActualValueModal,
    pendingActualValue,
    setPendingActualValue,
    handleActualValueSubmit,

    // Follow-Up modal (Not Qualified / Closed Lost)
    followUpModal,
    pendingFollowUp,
    setPendingFollowUp,
    followUpError,
    handleFollowUpSubmit,
    handleFollowUpSkip,
    handleFollowUpCancel,

    // Open Tasks warning modal
    showTaskWarning,
    pendingStageChange,
    openTasksData,
    handleConfirmStageChange,
    handleCancelStageChange,
  };
}
